/* eslint-disable camelcase */
import { get, isNumber } from 'lodash-es';
import { getInvTreatmentList } from 'client/engagement-handlers/helper';
import { getInventoryTypesCount } from 'site-modules/shared/utils/inventory-utils/get-inventory-types-count';
import { getCorrectStatus } from 'site-modules/shared/components/inventory/helpers/vehicle-history-helper';
import { isHistoryChanged } from 'site-modules/shared/utils/inventory-utils/is-history-changed';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import {
  DEFAULT_DELIVERY_CODE,
  MATCH_RESULT,
  NEGATIVE,
  PHOTO_TYPE,
  POSITIVE,
} from 'client/engagement-handlers/inventory-engagement-handler/constants';

/**
 * Lazy builder for inventory list tracking payloads.
 *
 * Each `with*()` method registers extractors; `build()` iterates active
 * inventories once and joins all collected values.
 *
 * Usage:
 *   new TrackingPayloadBuilder(inventories)
 *     .withSummary(totalCount, attributes)
 *     .withIdentity()
 *     .withPrice()
 *     .withPriceDrop()
 *     .withDealer()
 *     .withDeals()
 *     .withPhotos(photo360, defaults)
 *     .withDistance()
 *     .withDelivery(isNational, radius)
 *     .withHistory()
 *     .build();
 */
export class TrackingPayloadBuilder {
  #inventories;
  #activeInventories;
  #extractors = [];
  #summary = {};

  /**
   * @param {object[]} inventories - all inventory items (including sold)
   */
  constructor(inventories) {
    this.#inventories = inventories;
    this.#activeInventories = inventories.filter(inv => !inv.isSoldVin);
  }

  /**
   * Adds summary-level metrics (counts, sort info, CPO status).
   * @param {number} totalCount
   * @param {object} [attributes]
   * @returns {TrackingPayloadBuilder}
   */
  withSummary(totalCount, attributes) {
    const {
      CPO: cpo_results,
      NEW: new_results,
      USED: used_results,
      PRIVATE_PARTY: private_party_results,
    } = getInventoryTypesCount(this.#inventories);
    const matchResults = this.#inventories.length;
    const sortByAlgorithm = get(attributes, 'sort', '');

    Object.assign(this.#summary, {
      cpo_results: `${cpo_results}`,
      new_results: `${new_results}`,
      used_results: `${used_results}`,
      private_party_results: `${private_party_results}`,
      match_total: `${totalCount || matchResults}`,
      match_results: `${matchResults}`,
      cpo: cpo_results ? MATCH_RESULT.CPO : MATCH_RESULT.NOT_CPO,
      inv_treatment_list: getInvTreatmentList(this.#inventories),
      sort_algo_yes_no: sortByAlgorithm ? POSITIVE : NEGATIVE,
      sort_algo_name: sortByAlgorithm,
    });
    return this;
  }

  /**
   * Adds inventory identity fields (vid, vin, franchise, rooftop).
   * @returns {TrackingPayloadBuilder}
   */
  withIdentity() {
    this.#extractors.push(
      { key: 'inv_id_list', extract: inv => get(inv, 'vid', '') },
      { key: 'vin_list', extract: inv => get(inv, 'vin', '') },
      {
        key: 'f_id_list',
        extract: inv =>
          isPrivateParty(inv)
            ? get(inv, 'sellerInfo.franchiseId')
            : get(inv, 'dealerInfo.franchiseId', get(inv, 'dealerInfo.id')),
      },
      {
        key: 'loc_id_list',
        extract: inv =>
          isPrivateParty(inv)
            ? get(inv, 'sellerInfo.rooftopId', get(inv, 'sellerInfo.franchiseId'))
            : get(inv, 'dealerInfo.rooftopId'),
      }
    );
    return this;
  }

  /**
   * Adds advertised price per inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withPrice() {
    this.#extractors.push({
      key: 'price',
      extract: inv => get(inv, 'prices.advertisedPrice', ''),
    });
    return this;
  }

  /**
   * Adds advertised price delta for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withPriceDrop() {
    this.#extractors.push({
      key: 'price_drop',
      extract: inv => get(inv, 'prices.advertisedPriceDelta', ''),
    });
    return this;
  }

  /**
   * Adds total price change for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withTotalPriceChange() {
    this.#extractors.push({
      key: 'total_price_change',
      extract: inv =>
        isHistoryChanged(inv) ? get(inv, 'computedDisplayInfo.priceHistoryInfo.totalPriceChange', '') : '',
    });
    return this;
  }

  /**
   * Adds recommended offer for each inventory item.
   * New vehicles: edmundsSuggestedPrice, Used vehicles: listPriceEstimate.
   * @returns {TrackingPayloadBuilder}
   */
  withRecommendedOffer() {
    this.#extractors.push({
      key: 'recommended_offer',
      extract: inv =>
        isNew(get(inv, 'type', ''))
          ? get(inv, 'prices.edmundsSuggestedPrice', '')
          : get(inv, 'computedDisplayInfo.priceValidation.listPriceEstimate', ''),
    });
    return this;
  }

  /**
   * Adds below-market discount for each inventory item.
   * New vehicles: savings, Used vehicles: discountPrice.
   * @returns {TrackingPayloadBuilder}
   */
  withBelowMarket() {
    this.#extractors.push({
      key: 'below_market',
      extract: inv =>
        isNew(get(inv, 'type', ''))
          ? get(inv, 'prices.savings', '') || get(inv, 'prices.pricePromise.savings', '')
          : get(inv, 'computedDisplayInfo.priceValidation.priceDiscount', ''),
    });
    return this;
  }

  /**
   * Adds dealer rating and five-star status for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withDealer() {
    this.#extractors.push(
      { key: 'dealer_rating', extract: inv => get(inv, 'dealerInfo.displayInfo.salesOverallRating') },
      {
        key: 'five_star_dealer',
        extract: inv => (get(inv, 'dealerInfo.displayInfo.fiveStarYears', []).length ? POSITIVE : NEGATIVE),
      }
    );
    return this;
  }

  /**
   * Adds deal indicator and days listed for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withDeals() {
    this.#extractors.push(
      { key: 'deal_indicator', extract: inv => get(inv, 'computedDisplayInfo.priceValidation.dealType', '') },
      { key: 'days_listed', extract: inv => get(inv, 'computedDisplayInfo.daysOnEdmunds', '') }
    );
    return this;
  }

  /**
   * Adds photo type(s) for each inventory item.
   * One inventory may produce multiple types (360 + dealer photo).
   * @param {object} [photo360] - photo360 config with provider name
   * @param {object} [defaults] - defaults with defaultNoImageType
   * @returns {TrackingPayloadBuilder}
   */
  withPhotos(photo360, defaults = {}) {
    this.#extractors.push({
      key: 'photo_type',
      extract: inv => {
        const types = [];
        const dealerPhotos = get(inv, 'vehicleInfo.photo.defaultPhoto.largePhotoCount');

        if (photo360) {
          if (get(inv, `vehicleInfo.photo.photo360.${photo360.provider}.hasExterior`)) {
            types.push(PHOTO_TYPE.CAR_EXTERIOR);
          }
          if (get(inv, `vehicleInfo.photo.photo360.${photo360.provider}.hasInterior`)) {
            types.push(PHOTO_TYPE.CAR_INTERIOR);
          }
        }

        if (dealerPhotos) {
          types.push(isPrivateParty(inv) ? PHOTO_TYPE.PRIVATE_PARTY : PHOTO_TYPE.DEALER);
        } else {
          types.push(get(inv, 'stockPhotoInfo.photos[0].provider', defaults.defaultNoImageType || PHOTO_TYPE.NO_IMAGE));
        }

        return types;
      },
    });
    return this;
  }

  /**
   * Adds miles-away distance for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withDistance() {
    this.#extractors.push({
      key: 'miles_away',
      extract: inv => {
        const distance = get(inv, 'dealerInfo.distance');
        return isNumber(distance) ? distance.toFixed(1) : '';
      },
    });
    return this;
  }

  /**
   * Adds delivery match type for each inventory item.
   * @param {boolean} isNational
   * @param {number} radius
   * @returns {TrackingPayloadBuilder}
   */
  withDelivery(isNational, radius) {
    this.#extractors.push({
      key: 'match_type_list',
      extract: inv => {
        const invRadius = Math.round(get(inv, 'dealerInfo.distance', 0));
        const code =
          isNational || radius < invRadius
            ? get(inv, 'computedDisplayInfo.deliveryOptions.deliveryCode', DEFAULT_DELIVERY_CODE)
            : DEFAULT_DELIVERY_CODE;
        return MATCH_RESULT.DELIVERY[code];
      },
    });
    return this;
  }

  /**
   * Adds vehicle history status for each inventory item.
   * @returns {TrackingPayloadBuilder}
   */
  withHistory() {
    this.#extractors.push({
      key: 'vehicle_history',
      extract: inv =>
        getCorrectStatus(get(inv, 'historyInfo.freeHistoryReport'), get(inv, 'historyInfo.historyProvider', '')),
    });
    return this;
  }

  /*
   * Conditionally applies callback with this builder as argument.
   * Useful for applying certain extractors only for a subset of inventories
   * without creating multiple builder instances.
   *
   * @param {boolean} condition
   * @param {function(TrackingPayloadBuilder): TrackingPayloadBuilder} callback
   * @returns {TrackingPayloadBuilder}
   */
  applyIf(condition, callback) {
    if (condition) {
      callback(this);
    }
    return this;
  }

  /**
   * Iterates active inventories once, applies all registered extractors,
   * joins values and returns the final tracking payload object.
   * @returns {object}
   */
  build() {
    const collectors = {};
    this.#extractors.forEach(({ key }) => {
      collectors[key] = [];
    });

    this.#activeInventories.forEach(inv => {
      this.#extractors.forEach(({ key, extract }) => {
        const value = extract(inv);
        if (Array.isArray(value)) {
          collectors[key].push(...value);
        } else {
          collectors[key].push(value);
        }
      });
    });

    const result = { ...this.#summary };
    this.#extractors.forEach(({ key }) => {
      result[key] = collectors[key].join();
    });

    return result;
  }
}

/**
 * Extracts first vehicle info for tracking envelope.
 * @param {object[]} inventories
 * @returns {object}
 */
export function getFirstVehicle(inventories) {
  const firstVehicle = inventories[0] || {};

  return {
    make: get(firstVehicle, 'vehicleInfo.styleInfo.make', ''),
    model: get(firstVehicle, 'vehicleInfo.styleInfo.model', ''),
    year: get(firstVehicle, 'vehicleInfo.styleInfo.year', ''),
    type: get(firstVehicle, 'type', ''),
    interiorColor: get(firstVehicle, 'vehicleInfo.vehicleColors.interior.genericName', ''),
    exteriorColor: get(firstVehicle, 'vehicleInfo.vehicleColors.exterior.genericName', ''),
    price: get(firstVehicle, 'prices.dealerMsrp', ''),
    salePrice: get(firstVehicle, 'prices.advertisedPrice', ''),
    trim: get(firstVehicle, 'vehicleInfo.styleInfo.trim', ''),
    bodyType: get(firstVehicle, 'vehicleInfo.styleInfo.bodyType', ''),
  };
}
